import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import PoweredBy from '@/components/PoweredBy/PoweredByView.vue';

import { get_token, get_user_details, getCookie } from '@/router/index.js';
import Swal from 'sweetalert2';

export default {
    name: 'SettingsView',
    components: {
        Sidebar,
        Navbar,
        PoweredBy
    },

    data: () => ({
        // System Colors Data
        user: get_user_details(),
        primary_color: '#FF0004',
        secondary_color: '#000',
        accent: '#950002',
        logos: {
        logo: null,
        favicon: null
        },
        logo_preview: {
        logo: null,
        favicon: null
        },
        saving: false,
        save_success: false,
        show_palette_suggestions: false,
        palette_suggestions: [
        { name: 'Corporate Blue', primary: '#0066CC', secondary: '#003D7A', accent: '#FFB300' },
        { name: 'Professional Green', primary: '#2E7D32', secondary: '#1B5E20', accent: '#FFA000' },
        { name: 'Modern Purple', primary: '#7B1FA2', secondary: '#4A148C', accent: '#FF6F00' },
        { name: 'Tech Teal', primary: '#00897B', secondary: '#00695C', accent: '#FF5722' },
        { name: 'Bold Red', primary: '#C62828', secondary: '#8E0000', accent: '#FFC107' },
        { name: 'Classic Navy', primary: '#1A237E', secondary: '#0D47A1', accent: '#FF9800' }
        ]
    }),

    mounted() {
        this.csrfToken = getCookie("csrftoken");
        this.load_current_colors();
        this.update_css_variables();
    },

    watch: {
        'primary_color': 'update_css_variables',
        'secondary_color': 'update_css_variables',
        'accent': 'update_css_variables',

        show_palette_suggestions(val) {
            if (val) {
            this.$nextTick(() => {
                this.update_palette_swatches();
            });
            }
        },
    },

  methods: {

    update_css_variables() {
      // Update CSS variables when colors change
      this.$nextTick(() => {
        const container = this.$refs.preview_container;
        if (container) {
          container.style.setProperty('--primary-color', this.primary_color);
          container.style.setProperty('--secondary-color', this.secondary_color);
          container.style.setProperty('--red-dark-color', this.accent);
        }
        if (this.show_palette_suggestions) {
          this.update_palette_swatches();
        }
      });
    },

    update_palette_swatches() {
      this.palette_suggestions.forEach((palette, index) => {
        const ref_name = 'paletteCard' + index;
        const cards = Reflect.get(this.$refs, ref_name);
        const card = Array.isArray(cards) ? cards[0] : cards;
    
        if (card) {
          card.style.setProperty('--primary-color', palette.primary);
          card.style.setProperty('--secondary-color', palette.secondary);
          card.style.setProperty('--red-dark-color', palette.accent);
        }
      });
    },

    // System Colors Methods
    async load_current_colors() {
      const token = get_token();
      const org_id = this.user?.company_id || this.user?.organization_id;
      
      if (!token || !org_id) {
        return;
      }

      try {
        // Load colors
        const color_url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_organization_colors/${org_id}/`;
        
        const color_res = await fetch(color_url, {
          method: 'GET',
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Token ${token}`, 
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        });
        
        if (color_res.ok) {
          const payload = await color_res.json();
          const d = payload?.data || {};
          this.primary_color = d.primary_color || '#FF0004';
          this.secondary_color = d.secondary_color || '#000';
          this.accent = d.accent || '#950002';

        } else {
          console.error('Failed to load colors:', color_res.status);
        }

        // Load logos
        const logo_url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_organization_logos/${org_id}/`;
        const logo_res = await fetch(logo_url, {
          method: 'GET',
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Token ${token}`, 
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        });
        
        if (logo_res.ok) {
          const payload = await logo_res.json();
          const d = payload?.data || {};
          this.logo_preview.logo = d.logo || null;
          this.logo_preview.favicon = d.favicon || null;
        } else {
          console.error('Failed to load logos:', logo_res.status);
        }
      } catch (e) {
        console.error('Failed to load branding settings', e);
      }
    },

    async save_configuration() {
      this.saving = true;
      
      try {
        const token = get_token();
        const org_id = this.user?.company_id || this.user?.organization_id;
        
        if (!token || !org_id) {
          this.$notyf.error('Missing authentication or organization ID');
          return;
        }

        // Save colors
        const color_url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/update_organization_colors/${org_id}/`;

        const colors_payload = {
          primary_color: this.primary_color,
          secondary_color: this.secondary_color,
          accent: this.accent
        };
        
        const color_res = await fetch(color_url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          },
          body: JSON.stringify(colors_payload)
        });

        if (!color_res.ok) {
          const error_data = await color_res.json();
          this.$notyf.error('Color save failed:', error_data);
        }
        
        // Upload logos if any
        if (this.logos.logo || this.logos.favicon) {
          const form = new FormData();
          if (this.logos.logo) form.append('logo', this.logos.logo);
          if (this.logos.favicon) form.append('favicon', this.logos.favicon);

          const logo_url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/upload_organization_logos/${org_id}/`;
          const logo_res = await fetch(logo_url, {
            method: 'POST',
            credentials: 'include',
            headers: { 
              'Authorization': `Token ${token}`, 
              'X-CSRFToken': this.csrfToken, 
              'X-Page-URL': window.location.href 
            },
            body: form
          });

          if (!logo_res.ok) {
            const error_data = await logo_res.json();
            this.$notyf.error('Logo upload failed:', error_data);
          }
          
          const logo_result = await logo_res.json();
          
          // Update previews with S3 URLs
          if (logo_result.data) {
            this.logo_preview.logo = logo_result.data.logo || this.logo_preview.logo;
            this.logo_preview.favicon = logo_result.data.favicon || this.logo_preview.favicon;
          }
          
          // Clear file objects after successful upload
          this.logos = { logo: null, favicon: null };
        }

        this.save_success = true;
        this.$notyf.success('Configuration saved successfully');

        window.location.reload();
        
        setTimeout(() => {
          this.save_success = false;
        }, 3000);

      } catch (e) {
        this.$notyf.error(e.message || 'Failed to save configuration');
      } finally {
        this.saving = false;
      }
    },

    async reset_to_defaults() {
      const result = await Swal.fire({
        title: 'Reset to Defaults?',
        text: 'This will reset the form to default values.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Reset Form',
        cancelButtonText: 'Cancel'
      });
      if (!result.isConfirmed) return;
      const token = get_token();
      const org_id = this.user?.company_id || this.user?.organization_id;
      if (!token || !org_id) {
        this.$notyf.error('Missing authentication or organization ID');
        return;
      }
      this.saving = true;
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/reset_organization_branding/${org_id}/`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          },
          body: JSON.stringify({})
        });

        if (!response.ok) {
          const error_data = await response.json();
          this.$notyf.error('Failed to reset branding', error_data);
        }

        // Reset local state to defaults
        this.primary_color = '#FF0004';
        this.secondary_color = '#000';
        this.accent = '#950002';
        this.logos = { logo: null, favicon: null };
        this.logo_preview = { logo: null, favicon: null };
        this.save_success = false;

        this.$notyf.success('Branding reset to system defaults successfully');
        
        // Reload to fetch system defaults from server
        await this.load_current_colors();
        window.location.reload();
        
      } catch (e) {
        this.$notyf.error(e.message || 'Failed to reset branding');
      } finally {
        this.saving = false;
      }
    },

    check_color_contrast(bgColor, textColor = '#FFFFFF') {
      const getL = (hex) => {
        try {
          const rgb = parseInt(String(hex || '#ffffff').slice(1), 16);
          const r = ((rgb >> 16) & 0xff) / 255;
          const g = ((rgb >> 8) & 0xff) / 255;
          const b = (rgb & 0xff) / 255;
          const t = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
          return 0.2126 * t(r) + 0.7152 * t(g) + 0.0722 * t(b);
        } catch { return 1; }
      };
      
      try {
        const l1 = getL(bgColor), l2 = getL(textColor);
        const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
        return ratio >= 4.5;
      } catch { return true; }
    },

    apply_palette(palette) {
      this.primary_color = palette.primary;
      this.secondary_color = palette.secondary;
      this.accent = palette.accent;
      this.show_palette_suggestions = false;
      this.save_success = false;
    },

    trigger_file_input(type) {
      if (type === 'logo') this.$refs.logoInput.click();
      if (type === 'favicon') this.$refs.faviconInput.click();
    },

    handle_file_upload(type, event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size
      if (file.size > 2 * 1024 * 1024) {
        this.$notyf.error('File size must be less than 2MB');
        return;
      }

      // SECURITY: Validate file type
      const allowed_types = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowed_types.includes(file.type)) {
        this.$notyf.error('Invalid file type. Only images are allowed (JPEG, PNG, GIF, SVG)');
        return;
      }

      const allowed_logo_types = ['logo', 'favicon'];
      if (!allowed_logo_types.includes(type)) {
        this.$notyf.error('Invalid upload type');
        return;
      }

      Reflect.set(this.logos, type, file);
      const reader = new FileReader();
      
      // SECURITY: Add error handler
      reader.onerror = () => {
        this.$notyf.error('Failed to read file');
      };

      reader.onloadend = () => {
        const result = reader.result;
        
        if (typeof result !== 'string') {
          this.$notyf.error('Invalid file data');
          return;
        }

        const data_url_pattern = /^data:image\/(jpeg|jpg|png|gif|svg\+xml);base64,/;
        if (!data_url_pattern.test(result)) {
          this.$notyf.error('Invalid image data format');
          return;
        }

        // Safe to assign after all validations
        Reflect.set(this.logo_preview, type, result);
      };

      reader.readAsDataURL(file);
      this.save_success = false;
    }
  }

};